/**
 * Configuración de presentación del tenant: idioma, huso, moneda, marca.
 *
 * **Por qué el locale es del tenant y no del navegador.** Un investigador de
 * brand protection en Asunción puede tener el Chrome en inglés; si el
 * dashboard le formatea `03/04` como `MM/DD`, lee mal una fecha de escaneo y
 * abre un caso equivocado. El dato pertenece a la marca —sus lotes, sus
 * vencimientos, su huso— así que se muestra como la marca lo escribe. Lo que
 * sí puede elegir la persona es el **idioma de la interfaz**; eso no cambia
 * cómo se formatea un número.
 *
 * El huso es igual de importante: "escaneos de hoy" tiene que significar el
 * día del tenant. Con el huso del navegador, un analista en Madrid ve un
 * corte distinto que su colega en Asunción sobre los mismos datos.
 *
 * De dónde sale
 * -------------
 * Hoy: `/tenants/<slug>.json`, asset estático servido junto al dashboard.
 * Es la misma convención que `web/consumer` usa para la marca blanca
 * (`/brands/<slug>.json`), y por el mismo motivo: no cuesta un endpoint y no
 * bloquea a este issue esperando a que exista uno.
 *
 * Cuando la API exponga `GET /api/v1/tenant/settings`, se cambia
 * `fetchTenantSettings` y nada más. El resto del dashboard consume el
 * contexto, no el origen.
 *
 * Todo lo que llega se **sanea**: es JSON editable por operaciones y termina
 * en el DOM y dentro de `Intl`.
 */
import type { Language } from '@/i18n/languages';

export interface TenantSettings {
  slug: string;
  /** Nombre visible de la marca. */
  name: string;
  /** BCP-47 completo. Es lo que se le pasa a `Intl`. */
  locale: string;
  /** IANA. `America/Asuncion`, `America/Sao_Paulo`, … */
  timeZone: string;
  /** ISO-4217. Para importes de órdenes de etiquetas. */
  currency: string;
  /** Idioma por defecto de la interfaz para este tenant. */
  language: Language;
}

export const DEFAULT_TENANT_SETTINGS: TenantSettings = {
  slug: 'xyvona',
  name: 'XYVONA',
  locale: 'es-PY',
  timeZone: 'America/Asuncion',
  currency: 'PYG',
  language: 'es',
};

const SLUG = /^[a-z0-9][a-z0-9-]{0,62}$/;
const LOCALE = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;
const TIME_ZONE = /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*$/;
const CURRENCY = /^[A-Z]{3}$/;

export function isTenantSlug(value: unknown): value is string {
  return typeof value === 'string' && SLUG.test(value);
}

function safeText(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  // Los controles no aportan nada y ensucian el layout de la cabecera.
  // eslint-disable-next-line no-control-regex -- filtrarlos es el objetivo
  const clean = value.replace(/[\u0000-\u001f\u007f]/g, ' ').trim();
  return clean.length === 0 ? null : clean.slice(0, max);
}

/**
 * Un locale o un huso inválido no se descarta en silencio: hace que `Intl`
 * lance en cada render. Se validan por forma y después se prueban de verdad.
 */
function usableLocale(value: unknown): string | null {
  if (typeof value !== 'string' || !LOCALE.test(value)) return null;
  try {
    new Intl.DateTimeFormat(value);
    return value;
  } catch {
    return null;
  }
}

function usableTimeZone(value: unknown): string | null {
  if (typeof value !== 'string' || !TIME_ZONE.test(value)) return null;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return value;
  } catch {
    return null;
  }
}

function usableLanguage(value: unknown): Language | null {
  return value === 'es' || value === 'en' || value === 'pt' ? value : null;
}

/** Convierte un JSON arbitrario en algo seguro de renderizar y de pasar a `Intl`. */
export function sanitizeTenantSettings(raw: unknown, slug: string): TenantSettings {
  const base: TenantSettings = { ...DEFAULT_TENANT_SETTINGS, slug };
  if (typeof raw !== 'object' || raw === null) return base;
  const src = raw as Record<string, unknown>;

  const locale = usableLocale(src['locale']);
  return {
    slug,
    name: safeText(src['name'], 80) ?? base.name,
    locale: locale ?? base.locale,
    timeZone: usableTimeZone(src['timeZone']) ?? base.timeZone,
    currency:
      typeof src['currency'] === 'string' && CURRENCY.test(src['currency'])
        ? src['currency']
        : base.currency,
    // Si no viene idioma explícito, se deriva del locale: un tenant con
    // `pt-BR` quiere la interfaz en portugués sin tener que decirlo dos veces.
    language:
      usableLanguage(src['language']) ??
      usableLanguage(locale?.slice(0, 2)) ??
      base.language,
  };
}

/** `null` si el tenant no tiene archivo: se usan los defaults, no es un error. */
export async function fetchTenantSettings(
  slug: string,
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<TenantSettings> {
  if (!isTenantSlug(slug)) return { ...DEFAULT_TENANT_SETTINGS };
  try {
    const response = await fetchImpl(`/tenants/${slug}.json`, {
      credentials: 'omit',
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) return { ...DEFAULT_TENANT_SETTINGS, slug };
    return sanitizeTenantSettings(await response.json(), slug);
  } catch {
    // Que no cargue la configuración de presentación no puede dejar sin
    // dashboard a nadie: se muestra con los defaults.
    return { ...DEFAULT_TENANT_SETTINGS, slug };
  }
}
